import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { pushSheet, popSheet } from '../sheetStack.js'

const ANIM_MS = 200

// Bottom-sheet generico: scivola dal basso, occupa l'altezza --kb-h (stessa
// metafora del numpad / della keyboard del sistema). Outside-click + Esc per
// chiudere. Usato per i picker che non hanno input testuali (date / time).
export function BottomSheet({ open, onClose, children, className = '' }) {
  const [mounted, setMounted] = useState(false)
  const [entered, setEntered] = useState(false)

  useEffect(() => {
    if (open) {
      setMounted(true)
      return
    }
    if (mounted) {
      const t = setTimeout(() => setMounted(false), ANIM_MS)
      return () => clearTimeout(t)
    }
  }, [open, mounted])

  // Toggle body.has-sheet so the app shell shrinks above the kb space
  useEffect(() => {
    if (!mounted) return
    pushSheet()
    return () => popSheet()
  }, [mounted])

  useLayoutEffect(() => {
    if (open && mounted) {
      const id = requestAnimationFrame(() => setEntered(true))
      return () => cancelAnimationFrame(id)
    }
    setEntered(false)
  }, [open, mounted])

  // Stable ref for onClose so the effects below don't re-run on every parent
  // render (which would cause a pushState/back loop, opening + closing).
  const onCloseRef = useRef(onClose)
  useEffect(() => { onCloseRef.current = onClose }, [onClose])

  // Outside click
  useEffect(() => {
    if (!open) return
    function onPointerDown(e) {
      const sheets = document.querySelectorAll('.bsheet, .numpad')
      for (const el of sheets) {
        if (el.contains(e.target)) return
      }
      onCloseRef.current()
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('touchstart', onPointerDown, { passive: true })
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('touchstart', onPointerDown)
    }
  }, [open])

  // Esc
  useEffect(() => {
    if (!open) return
    function onKey(e) { if (e.key === 'Escape') onCloseRef.current() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  // Back-button (Android / browser) closes the sheet instead of navigating
  const closedByPopRef = useRef(false)
  useEffect(() => {
    if (!open) return
    history.pushState({ sheet: true }, '')
    closedByPopRef.current = false
    function onPop() {
      closedByPopRef.current = true
      onCloseRef.current()
    }
    window.addEventListener('popstate', onPop)
    return () => {
      window.removeEventListener('popstate', onPop)
      if (!closedByPopRef.current && history.state && history.state.sheet) {
        history.back()
      }
    }
  }, [open])

  if (!mounted) return null

  return createPortal(
    <div className={`bsheet${entered ? ' bsheet-entered' : ''} ${className}`}>
      {children}
    </div>,
    document.body,
  )
}
